import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

import type { BaseQuery } from './baseQuery.types';

type GetBudgetGroupByUserResponse = components['schemas']['GetBudgetGroupByUserEndpoint_GetBudgetGroupByUserEndpointPagedResponse'];

export interface UseBudgetGroupByUserReturn extends BaseQuery {
  budgetGroups: GetBudgetGroupByUserResponse | null;
  totalCount: number;
}

export function useBudgetGroupByUser(
  page: number = 1,
  pageSize: number = 50,
  userId: string,
  enabled: boolean = true,
): UseBudgetGroupByUserReturn {
  const { organizationId } = useOrganizationId();
  const { getAccessToken } = useAuth();

  const getBudgetGroupByUser = async (page: number, pageSize: number, organizationId: number, userId: string) => {
    const client = createApiClient(getAccessToken);
    const { data } = await client.GET('/api/organizations/{organizationId}/users/{userId}/budget-groups', {
      params: {
        path: { organizationId, userId: userId },
        query: {
          Page: page,
          PageSize: pageSize,
        },
      },
    });

    if (!data) {
      throw new Error('No data returned from API');
    }

    return data as GetBudgetGroupByUserResponse;
  };

  const isQueryEnabled = enabled && !!userId && organizationId > 0;

  const useBudgetGroupByUserQuery = useQuery({
    queryKey: ['budgetGroups', page, pageSize, organizationId, userId],
    queryFn: () => getBudgetGroupByUser(page, pageSize, organizationId, userId),
    enabled: isQueryEnabled,
    retry: false,
  });

  const { data, isLoading, isPending, error, refetch, isRefetching } = useBudgetGroupByUserQuery;

  return {
    budgetGroups: data ?? null,
    totalCount: data?.totalCount ?? 0,
    isLoading: isQueryEnabled && (isLoading || isPending),
    isRefetching,
    error: error as Error | null,
    refetch,
  };
}
